<template>
  <main-template>
    <v-row>
      <v-col>
        <span class="text-h4">Etapas</span>
        <e-help-tooltip :text="$constants.tooltips.turmas.criarAno" />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="pt-0 pb-0">
        <div class="d-flex mb-2 justify-space-between">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            autofocus
            class="ml-2 search-input"
            dense
            hide-details
            label="Pesquisar..."
            single-line
            solo
          />
        </div>
      </v-col>

      <v-col class="pt-0">
        <v-data-table
          :custom-filter="dataTableFilter"
          :headers="table.headers"
          :items="seriesList"
          :items-per-page="10"
          :loading="carregandoSeries"
          :search="search"
          class="elevation-1"
          hide-default-footer
        >
          <template v-slot:body="{ items }">
            <tbody is="transition-group" duration="150" name="scroll-x-transition">
              <tr
                v-for="serie of items"
                :key="serie.id"
                :class="serie.estaDeletado ? ['deleted'] : []"
                color="primary"
              >
                <td>{{ serie.id }}</td>
                <td>{{ serie.ano ? serie.ano.descricao : "- - -" }}</td>
                <td>{{ serie.descricao }}</td>

                <td>
                  <v-tooltip v-if="!serie.estaDeletado" bottom>
                    <template v-slot:activator="{ attrs, on }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        class="ml-1"
                        color="blue-grey lighten-3"
                        x-small
                        @click="() => editarSerie(serie)"
                      >
                        <v-icon small>fa fa-edit</v-icon>
                      </v-btn>
                    </template>
                    <span>Editar etapa</span>
                  </v-tooltip>

                  <v-tooltip v-if="serie.estaDeletado" bottom>
                    <template v-slot:activator="{ attrs, on }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        class="ml-1"
                        color="info"
                        x-small
                        @click="() => recuperarSerie(serie)"
                      >
                        <v-icon small>fa fa-sync</v-icon>
                      </v-btn>
                    </template>
                    <span>Recuperar etapa</span>
                  </v-tooltip>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>

        <e-paginate
          :current_page="paginate.current_page"
          :last_page="paginate.last_page"
          :pageNumber="pageNumber"
          :search="search"
          @changeActionResponse="listarSeries"
        />
      </v-col>
    </v-row>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";
import { dataTableFilter } from "@/plugins/searchQuery";
import Serie from "@/Models/Serie";

export default {
  computed: {
    ...mapGetters("Series", ["carregandoSeries", "seriesList"]),
  },

  data() {
    return {
      dataTableFilter,
      pageNumber: 0,
      paginate: {
        current_page: 0,
        last_page: 0,
      },
      search: "",
      seriesList: [],
      table: {
        headers: [
          { text: "#", value: "id" },
          { text: "Ano", value: "ano" },
          { text: "Nome", value: "descricao" },
          { text: "Ações", value: "actions", sortable: false },
        ],
      },
    };
  },

  mounted() {
    this.carregandoSeries = true;
    this.$store.dispatch("Series/loadSeries");
    this.listarSeries(this.pageNumber);
    this.carregandoSeries = true;
  },

  watch: {
    carregandoSeries(value) {
      if (value && this.seriesList.length === 0) {
        this.$loaderService.open("Carregando series");
      } else {
        this.$loaderService.close();
      }
    },

    async search(val) {
      if (val.length > 2) {
        await this.listarSeries(0, val);
      }
      if (!val) {
        await this.listarSeries(0);
      }
    },
  },

  methods: {
    criarSerie() {
      this.$router.push({ name: "series.create" });
    },

    async listarSeries(pageNumber, query = null) {
      this.carregandoSeries = true;
      try {
        const response = await this.$services.seriesService.syncAllPaginated(pageNumber, query);
        this.seriesList = await response.data.map((serie) => new Serie(serie));
        this.paginate = response;
      } catch (error) {
        this.$handleError(error);
      }
      this.carregandoSeries = false;
    },

    editarSerie(serie) {
      this.$router.push({ name: "series.edit", params: { serie_id: serie.id } });
    },

    async deletarSerie(serie) {
      this.$modals.danger({
        buttons: [
          {
            callback: () => {
              this.$modals.close();
            },
            props: {
              color: "warning",
              small: true,
            },
            text: "Cancelar",
          },
          {
            callback: async () => {
              this.$modals.close();
              this.$loaderService.open("Deletando a Etapa");
              try {
                await this.$services.seriesService.deletarSerie(serie);
                this.$toast.success("Etapa deletada com sucesso");
                await this.listarSeries();
              } catch (error) {
                this.$handleError(error);
              }
              this.$loaderService.close();
            },
            disableIfCodeDoesntMatch: true,
            props: {
              color: "error",
              small: true,
            },
            text: "Confirmar",
          },
        ],
        confirmationCode: serie.id,
        confirmationMessage: `Por favor, digite <strong>${serie.id}</strong> para confirmar`,
        message: "Tem certeza que deseja apagar esta etapa?",
        title: "Cuidado!",
      });
    },

    recuperarSerie(serie) {
      this.$modals.warn({
        buttons: [
          {
            callback: () => {
              this.$modals.close();
            },
            props: {
              color: "warning",
              small: true,
            },
            text: "Cancelar",
          },
          {
            callback: async () => {
              this.$modals.close();
              this.$loaderService.open("Recuperando a Etapa");
              try {
                await this.$services.seriesService.recuperarSerie(serie);
                this.$toast.success("Etapa recuperada com sucesso");
                await this.listarSeries();
              } catch (error) {
                this.$handleError(error);
              }
              this.$loaderService.close();
            },
            disableIfCodeDoesntMatch: true,
            props: {
              color: "error",
              small: true,
            },
            text: "Confirmar",
          },
        ],
        confirmationCode: serie.id,
        confirmationMessage: `Por favor, digite <strong>${serie.id}</strong> para confirmar`,
        message: "Tem certeza que deseja recuperar esta etapa?",
        title: "Atenção!",
      });
    },
  },
};
</script>

<style></style>
